import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import {
  DownloadSimple, DeviceMobile, AppleLogo, AndroidLogo, Browsers, Warning,
} from '@phosphor-icons/react';

/**
 * Cross-platform "Add Arcanum to your home screen" guide.
 *
 * - Android/Chrome: catches `beforeinstallprompt` and exposes a one-tap install button.
 * - iOS Safari: shows the Share -> Add to Home Screen instructions.
 * - In-app browsers (Instagram, Facebook, X, LinkedIn, TikTok, ...): warns the user
 *   to open the link in Safari/Chrome first; the OS-level install/share menus
 *   are not exposed inside those WebViews.
 * - Hides itself when the app is already running standalone.
 */

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator && window.navigator.standalone) return true;
  return false;
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Common in-app webviews where the share / install menus are missing or hidden.
  return /Instagram|FBAN|FBAV|FB_IAB|Twitter|Line\/|MicroMessenger|TikTok|LinkedInApp|Snapchat/i.test(ua);
}

const InstallGuide = ({ compact = false }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const platform = detectPlatform();
  const inApp = detectInAppBrowser();

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } finally { setDeferred(null); }
  }, [deferred]);

  if (isStandalone() || installed) {
    return (
      <div
        className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
        data-testid="install-guide-already-installed"
      >
        Arcanum is installed on this device.
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-border bg-card ${compact ? 'p-4' : 'p-5'} space-y-3`}
      data-testid="install-guide"
    >
      <div className="flex items-center gap-2">
        <DeviceMobile className="w-5 h-5 text-primary" weight="fill" />
        <p className="font-heading font-medium text-foreground">Add Arcanum to your home screen</p>
      </div>

      {inApp && (
        <div
          className="flex items-start gap-2 text-xs rounded-lg border border-secondary/30 bg-secondary/10 p-2.5"
          data-testid="install-guide-inapp-warning"
        >
          <Warning className="w-4 h-4 shrink-0 mt-0.5 text-secondary" weight="fill" />
          <p className="text-foreground/80">
            You're in an in-app browser. Tap the menu and choose
            <span className="font-medium"> Open in {platform === 'ios' ? 'Safari' : 'Chrome'}</span>,
            then come back to install.
          </p>
        </div>
      )}

      {platform === 'android' && (
        <div className="space-y-2" data-testid="install-guide-android">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <AndroidLogo className="w-4 h-4" weight="fill" />
            <span>Android · Chrome</span>
          </div>
          {deferred ? (
            <Button
              onClick={triggerInstall}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="install-guide-android-button"
            >
              <DownloadSimple className="w-4 h-4 mr-2" />
              Install Arcanum
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              Tap <span className="font-medium text-foreground">Install app</span> in the address bar,
              or open the Chrome menu (⋮) → <span className="font-medium text-foreground">Add to Home screen</span> → <span className="font-medium text-foreground">Install</span>.
            </p>
          )}
        </div>
      )}

      {platform === 'ios' && (
        <div className="space-y-2" data-testid="install-guide-ios">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <AppleLogo className="w-4 h-4" weight="fill" />
            <span>iPhone · Safari</span>
          </div>
          <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Open this page in <span className="font-medium text-foreground">Safari</span></li>
            <li>Tap the <span className="font-medium text-foreground">Share</span> button</li>
            <li>Choose <span className="font-medium text-foreground">Add to Home Screen</span></li>
            <li>Tap <span className="font-medium text-foreground">Add</span></li>
          </ol>
        </div>
      )}

      {platform === 'other' && (
        <div className="space-y-2" data-testid="install-guide-desktop">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Browsers className="w-4 h-4" weight="fill" />
            <span>Desktop / other</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Open this link on your phone to install Arcanum on your home screen.
            On Android use Chrome; on iPhone use Safari.
          </p>
        </div>
      )}
    </div>
  );
};

export default InstallGuide;
